import type React from "react";
import { useCallback } from "react";
import type { BarSpan } from "./DensityChart.lib";
import type { WindowPart } from "./windowSpan.lib";
import { keySpan } from "./windowKeys.lib";

interface UseWindowKeysParams {
    span: BarSpan;
    barCount: number;
    minBar: number;
    /** Keys do nothing while a pointer drags the window. */
    isDragging: () => boolean;
    /** Before a key's change commits. */
    onKey: () => void;
    onChange: (span: BarSpan) => void;
}

/** Arrow keys on the window or a handle, committing each bar's step at once (`keySpan`). */
export function useWindowKeys({
    span,
    barCount,
    minBar,
    isDragging,
    onKey,
    onChange,
}: UseWindowKeysParams) {
    return useCallback(
        (mode: WindowPart, event: React.KeyboardEvent<HTMLElement>) => {
            if (isDragging()) {
                return;
            }
            const next = keySpan({
                span,
                mode,
                key: event.key,
                barCount,
                minBar,
            });
            if (!next) {
                return;
            }
            event.preventDefault();
            onKey();
            onChange(next);
        },
        [barCount, isDragging, minBar, onChange, onKey, span],
    );
}
